import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faRedo,
    faDownload,
    faFile,
    faChevronDown,
    faChevronRight,
    faTriangleExclamation,
    faFolderOpen,
    faCloudArrowUp,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/axiosConfig';
import { useFlashMessage } from '../FlashMessage';
import { getErrorMessage } from '../../utils/errorMessages';
import UploadToHubModal from './UploadToHubModal';
import '../CRMProfileScreen.css';

const CATEGORY_ORDER = [
    'shipment',
    'accounting',
    'legal',
    'bank_confirmation',
    'trade_finance',
    'knowledge',
    'other',
];

const CATEGORY_FALLBACK_LABELS = {
    shipment: 'Shipment Docs',
    accounting: 'Accounting Docs',
    legal: 'Legal',
    bank_confirmation: 'Bank Confirmation',
    trade_finance: 'Trade Finance Docs',
    knowledge: 'Knowledge Hub',
    other: 'Other',
};

const SOURCE_PILL_COLORS = {
    banking_verification: '#4a6b25',
    bank_statement: '#4a6b25',
    kyc_documents: '#701a75',
    coa_certificates: '#0891b2',
    ai_notes: '#10b981',
    general_partner_info: '#10b981',
    knowledge_hub: '#10b981',
    incoming_invoice: '#f59e0b',
    finance: '#f59e0b',
    stock_contract: '#dc2626',
    legal_contract: '#3a5b15',
    trade_finance: '#db2777',
    delivery: '#0d9488',
};

// Preserve appearance order; group a category's documents by their subgroup.
const groupDocumentsBySubgroup = (docs) => {
    const order = [];
    const groups = new Map();
    (docs || []).forEach((doc) => {
        const key = doc.subgroup || '';
        if (!groups.has(key)) {
            groups.set(key, { key, label: doc.subgroup_label || '', docs: [] });
            order.push(key);
        }
        groups.get(key).docs.push(doc);
    });
    return order.map((key) => groups.get(key));
};

const formatDate = (value) => {
    if (!value) return '—';
    try {
        let parsed = value;
        if (typeof value === 'string') {
            const netMatch = value.match(/\/Date\((\d+)\)\//);
            if (netMatch) {
                parsed = parseInt(netMatch[1], 10);
            }
        }
        const d = new Date(parsed);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });
    } catch (_err) {
        return value;
    }
};

const formatSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const triggerBrowserDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const CRMDocumentHubTab = ({ profileData, isNewProfile }) => {
    const { showError, showSuccess } = useFlashMessage();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [collapsedCategories, setCollapsedCategories] = useState({});
    const [downloadingId, setDownloadingId] = useState(null);
    const [showUpload, setShowUpload] = useState(false);

    const partnerId = profileData?.id;

    const fetchDocuments = useCallback(async () => {
        if (!partnerId) return;
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get(
                `/api/crm/profiles/${partnerId}/documents/`
            );
            setData(response.data);
        } catch (err) {
            console.error('Document Hub: failed to load documents', err);
            setError(getErrorMessage(err, 'Failed to load partner documents.'));
        } finally {
            setLoading(false);
        }
    }, [partnerId]);

    useEffect(() => {
        if (partnerId && !isNewProfile) {
            fetchDocuments();
        }
    }, [partnerId, isNewProfile, fetchDocuments]);

    const handleDownload = async (doc) => {
        if (!doc.download_url) {
            showError('Download is not available for this document.');
            return;
        }
        setDownloadingId(doc.id);
        try {
            const response = await axiosInstance.get(doc.download_url, {
                responseType: 'blob',
            });

            let filename = doc.filename;
            const contentDisposition =
                response.headers['content-disposition'] ||
                response.headers['Content-Disposition'];
            if (contentDisposition) {
                const match = contentDisposition.match(
                    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
                );
                if (match && match[1]) {
                    filename = match[1].replace(/['"]/g, '');
                }
            }

            triggerBrowserDownload(new Blob([response.data]), filename || 'document');
            showSuccess(`Downloaded "${filename}"`);
        } catch (err) {
            console.error('Document Hub: download failed', err);
            showError(getErrorMessage(err, 'Failed to download document.'));
        } finally {
            setDownloadingId(null);
        }
    };

    const toggleCategory = (categoryKey) => {
        setCollapsedCategories((prev) => ({
            ...prev,
            [categoryKey]: !prev[categoryKey],
        }));
    };

    const filteredCategories = useMemo(() => {
        if (!data?.categories) return [];

        const term = searchTerm.trim().toLowerCase();

        return CATEGORY_ORDER
            .filter((key) => data.categories[key])
            .map((key) => {
                const cat = data.categories[key];
                const documents = term
                    ? (cat.documents || []).filter((doc) => {
                        const haystack = [
                            doc.display_name,
                            doc.filename,
                            doc.source_label,
                            doc.subgroup_label,
                            doc.metadata?.subject,
                            doc.metadata?.classification,
                            doc.metadata?.contract_number,
                            doc.metadata?.title,
                        ]
                            .filter(Boolean)
                            .join(' ')
                            .toLowerCase();
                        return haystack.includes(term);
                    })
                    : (cat.documents || []);

                return {
                    key,
                    label: cat.label || CATEGORY_FALLBACK_LABELS[key] || key,
                    totalCount: cat.count || 0,
                    documents,
                };
            })
            .filter((cat) => cat.totalCount > 0);
    }, [data, searchTerm]);

    const visibleDocCount = useMemo(
        () => filteredCategories.reduce((sum, cat) => sum + cat.documents.length, 0),
        [filteredCategories]
    );

    if (isNewProfile || !partnerId) {
        return (
            <div className="crm-tab-panel">
                <div className="crm-form-section">
                    <div className="crm-placeholder-container">
                        <div className="crm-placeholder-header">
                            <div className="crm-placeholder-icon">
                                <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: '2.5rem' }} />
                            </div>
                            <h2 className="crm-placeholder-title">Document Management Hub</h2>
                            <p className="crm-placeholder-subtitle">
                                Save the partner profile first to view linked documents.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const renderDocument = (doc) => {
        const pillColor = SOURCE_PILL_COLORS[doc.source_type] || '#6b7280';
        const isDownloading = downloadingId === doc.id;
        return (
            <div
                key={doc.id}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    borderTop: '1px solid #f3f4f6',
                }}
            >
                <FontAwesomeIcon
                    icon={faFile}
                    style={{ color: '#6b7280', fontSize: '1.1rem' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            overflow: 'hidden',
                        }}
                    >
                        <span
                            style={{
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                color: '#111827',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            title={doc.display_name || doc.filename}
                        >
                            {doc.display_name || doc.filename}
                        </span>
                        {doc.ai_named && !doc.is_user_confirmed && (
                            <span
                                style={{
                                    flex: '0 0 auto',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.04em',
                                    color: '#fff',
                                    background: '#4a6b25',
                                    borderRadius: '3px',
                                    padding: '1px 5px',
                                }}
                                title="Auto-named by AI — not yet reviewed"
                            >
                                AI
                            </span>
                        )}
                        {doc.mirrored_from && (
                            <span
                                style={{
                                    flex: '0 0 auto',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.04em',
                                    color: '#0369a1',
                                    background: '#e0f2fe',
                                    border: '1px solid #bae6fd',
                                    borderRadius: '3px',
                                    padding: '1px 5px',
                                }}
                                title={`Also appears under ${doc.mirrored_from}`}
                            >
                                ALSO IN {String(doc.mirrored_from).toUpperCase()}
                            </span>
                        )}
                    </div>
                    {doc.display_name && doc.display_name !== doc.filename && (
                        <div
                            style={{
                                fontSize: '0.75rem',
                                color: '#888',
                                marginTop: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            title={doc.filename}
                        >
                            {doc.filename}
                        </div>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.5rem',
                            alignItems: 'center',
                            marginTop: '0.25rem',
                            fontSize: '0.78rem',
                            color: '#6b7280',
                        }}
                    >
                        <span
                            style={{
                                background: pillColor,
                                color: 'white',
                                padding: '0.1rem 0.55rem',
                                borderRadius: '999px',
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.02em',
                            }}
                        >
                            {doc.source_label}
                        </span>
                        <span>{formatDate(doc.uploaded_at)}</span>
                        {doc.file_size != null && (
                            <span>{formatSize(doc.file_size)}</span>
                        )}
                        {doc.metadata?.subject && (
                            <span title={doc.metadata.subject}>
                                · {doc.metadata.subject}
                            </span>
                        )}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={() => handleDownload(doc)}
                    disabled={!doc.download_url || isDownloading}
                    title={
                        doc.download_url
                            ? 'Download document'
                            : 'Download unavailable for this source'
                    }
                    style={{
                        background: !doc.download_url
                            ? '#e5e7eb'
                            : isDownloading
                                ? '#6c757d'
                                : 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                        color: !doc.download_url ? '#6b7280' : 'white',
                        border: 'none',
                        borderRadius: '0.4rem',
                        padding: '0.4rem 0.75rem',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor:
                            !doc.download_url || isDownloading
                                ? 'not-allowed'
                                : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                    }}
                >
                    <FontAwesomeIcon icon={faDownload} />
                    {isDownloading ? 'Downloading…' : 'Download'}
                </button>
            </div>
        );
    };

    const renderCategoryBody = (cat) => {
        const groups = groupDocumentsBySubgroup(cat.documents);
        const hasSubgroups = groups.some((g) => g.key !== '');
        if (!hasSubgroups) {
            return <div>{cat.documents.map(renderDocument)}</div>;
        }
        return (
            <div>
                {groups.map((group) => (
                    <div key={group.key || '_'}>
                        <div
                            style={{
                                padding: '0.4rem 1rem',
                                background: '#fafafa',
                                borderTop: '1px solid #f3f4f6',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: '#9ca3af',
                            }}
                        >
                            {group.label || group.key || 'General'}
                            <span style={{ marginLeft: '0.4rem', color: '#c0c4cc' }}>
                                ({group.docs.length})
                            </span>
                        </div>
                        {group.docs.map(renderDocument)}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="crm-tab-panel">
            <div className="crm-form-section">
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '0.75rem',
                        marginBottom: '1rem',
                        padding: '1rem',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                    }}
                >
                    <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                        <div style={{ fontWeight: 600, color: '#374151', fontSize: '1rem' }}>
                            Document Hub
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                            {loading
                                ? 'Loading documents…'
                                : data
                                    ? `${data.total_count} total document${data.total_count === 1 ? '' : 's'} linked to ${data.partner_name || 'this partner'}`
                                    : 'No documents loaded yet.'}
                        </div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem',
                            padding: '0.4rem 0.6rem',
                            flex: '1 1 240px',
                            minWidth: '220px',
                        }}
                    >
                        <FontAwesomeIcon icon={faSearch} style={{ color: '#6b7280' }} />
                        <input
                            type="text"
                            placeholder="Search filename, subject, source…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                border: 'none',
                                outline: 'none',
                                flex: 1,
                                fontSize: '0.875rem',
                            }}
                        />
                    </div>

                    <button
                        type="button"
                        onClick={fetchDocuments}
                        disabled={loading}
                        style={{
                            background: loading
                                ? '#6c757d'
                                : 'linear-gradient(135deg, #5a7b35, #4a6b25)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                        }}
                    >
                        <FontAwesomeIcon icon={faRedo} />
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </button>

                    <button
                        type="button"
                        onClick={() => setShowUpload(true)}
                        disabled={loading}
                        style={{
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                        }}
                    >
                        <FontAwesomeIcon icon={faCloudArrowUp} />
                        Upload
                    </button>
                </div>

                {data?.source_errors?.length > 0 && (
                    <div
                        style={{
                            background: '#fffbeb',
                            border: '1px solid #f59e0b',
                            borderRadius: '0.5rem',
                            padding: '0.75rem 1rem',
                            color: '#92400e',
                            marginBottom: '1rem',
                            display: 'flex',
                            gap: '0.6rem',
                            alignItems: 'flex-start',
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            style={{ marginTop: '0.15rem', color: '#d97706' }}
                        />
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                                Some sources failed to load
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                                {data.source_errors.map((err) => (
                                    <li key={err.source_type} style={{ fontSize: '0.85rem' }}>
                                        <strong>{err.source_label}:</strong> {err.error}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {loading && !data && (
                    <div
                        style={{
                            padding: '3rem',
                            textAlign: 'center',
                            color: '#6b7280',
                            background: '#f9fafb',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                        }}
                    >
                        Loading documents…
                    </div>
                )}

                {error && (
                    <div
                        style={{
                            padding: '1rem',
                            backgroundColor: '#fef2f2',
                            border: '1px solid #ef4444',
                            borderRadius: '0.5rem',
                            color: '#dc2626',
                            marginBottom: '1rem',
                        }}
                    >
                        {error}
                    </div>
                )}

                {!loading && !error && data && data.total_count === 0 && (
                    <div
                        style={{
                            padding: '3rem',
                            textAlign: 'center',
                            color: '#6b7280',
                            background: '#f9fafb',
                            border: '1px dashed #d1d5db',
                            borderRadius: '0.5rem',
                        }}
                    >
                        <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: '2rem', marginBottom: '0.5rem', display: 'block' }} />
                        No documents linked to this partner yet.
                        <div style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                            Documents uploaded via KYC, Banking, AI Notes and other tabs will appear here.
                        </div>
                    </div>
                )}

                {!loading && !error && data && data.total_count > 0 && visibleDocCount === 0 && searchTerm && (
                    <div
                        style={{
                            padding: '2rem',
                            textAlign: 'center',
                            color: '#6b7280',
                            background: '#f9fafb',
                            border: '1px dashed #d1d5db',
                            borderRadius: '0.5rem',
                        }}
                    >
                        No documents match "{searchTerm}".
                    </div>
                )}

                {filteredCategories.map((cat) => {
                    const collapsed = !!collapsedCategories[cat.key];
                    return (
                        <div
                            key={cat.key}
                            style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '0.5rem',
                                marginBottom: '1rem',
                                overflow: 'hidden',
                                background: 'white',
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => toggleCategory(cat.key)}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    background: '#f3f4f6',
                                    border: 'none',
                                    padding: '0.75rem 1rem',
                                    cursor: 'pointer',
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    color: '#374151',
                                }}
                            >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronDown} />
                                    {cat.label}
                                </span>
                                <span
                                    style={{
                                        background: '#e5e7eb',
                                        borderRadius: '999px',
                                        padding: '0.15rem 0.6rem',
                                        fontSize: '0.8rem',
                                        color: '#374151',
                                    }}
                                >
                                    {searchTerm && cat.documents.length !== cat.totalCount
                                        ? `${cat.documents.length} / ${cat.totalCount}`
                                        : cat.totalCount}
                                </span>
                            </button>

                            {!collapsed && cat.documents.length === 0 && searchTerm && (
                                <div
                                    style={{
                                        padding: '1rem',
                                        color: '#6b7280',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    No documents in this section match the search.
                                </div>
                            )}

                            {!collapsed && cat.documents.length > 0 && renderCategoryBody(cat)}
                        </div>
                    );
                })}
            </div>

            <UploadToHubModal
                isOpen={showUpload}
                onClose={() => setShowUpload(false)}
                profileId={partnerId}
                partnerName={data?.partner_name || profileData?.legal_entity_name}
                onUploaded={fetchDocuments}
            />
        </div>
    );
};

export default CRMDocumentHubTab;
